import * as React from 'react';
import { FunctionComponent, ReactNode, useState } from 'react';
import { Box, Button, SxProps } from '@mui/material';
import htmlTruncate from 'html-truncate';
import { sanitizeRichText } from '../../graphql/pimcore/modules/siteContent/tools/sanitizeRichText';
import { useCommonContent } from '../../graphql/pimcore/modules/siteContent/hooks/useCommonContent';

interface TextContentProps {
    name: string;
    content1: ReactNode;
    content2?: ReactNode;
    content3?: ReactNode;
    buttonText?: string;
    buttonSx?: SxProps;
    buttonHref?: string;
    expandable?: boolean;
    expandButtonSx?: SxProps;
}
const truncateReadMoreHtml = (
    html: string,
    maxLength: number
): { truncated: string; isTruncated: boolean } => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const text = tempDiv.textContent || tempDiv.innerText || '';

    const isTruncated = text.length > maxLength;
    const truncated = isTruncated ? htmlTruncate(html, maxLength, { ellipsis: '' }) : html;

    return { truncated, isTruncated };
};

const TextContent: FunctionComponent<TextContentProps> = ({
    name,
    content1,
    content2,
    content3,
    buttonText,
    buttonSx,
    buttonHref,
    expandable = false,
    expandButtonSx = {},
}) => {
    const { contentTree: cmsCommonContent } = useCommonContent();
    const [isExpanded, setIsExpanded] = useState(false);
    const maxTextLength = 1498;
    const readMoreLabel = cmsCommonContent.misc.readMoreButtonLabel.content;
    const readLessLabel = cmsCommonContent.misc.readLessButtonLabel.content;

    let htmlContent = '';
    if (typeof content1 === 'object' && content1 !== null && 'props' in content1) {
        const props = (content1 as any).props;
        if (props.dangerouslySetInnerHTML?.__html) {
            htmlContent = props.dangerouslySetInnerHTML.__html;
        }
    }
    const { truncated, isTruncated } = truncateReadMoreHtml(htmlContent, maxTextLength);

    const showContent =
        isExpanded || !isTruncated ? (
            content1
        ) : (
            <div
                dangerouslySetInnerHTML={{
                    __html: sanitizeRichText(truncated, ['ul', 'li', 'a'], ['href', 'target']),
                }}
            />
        );

    const handleClick = () => {
        if (expandable) {
            setIsExpanded((prev) => !prev);
        }
    };

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column', // 使内容垂直排列
                gap: '1rem',
                fontFamily: 'Poppins',
                fontSize: '16px',
                fontWeight: 400,
                lineHeight: '24px',
                textAlign: 'left', // 全局设置文本左对齐
            }}
            data-test-id={`${name}-text-area`}
        >
            {expandable && (
                <Box
                    component="div"
                    sx={{
                        '& a': {
                            color: '#B75332', // 想要的顏色
                            fontWeight: 500,
                        },
                        '& a:hover': {
                            color: '#E5673E',
                        },
                    }}
                >
                    {showContent}
                </Box>
            )}

            {!expandable && <>{typeof content1 === 'string' ? <p>{content1}</p> : content1}</>}
            {typeof content2 === 'string' ? <p>{content2}</p> : content2}
            {typeof content3 === 'string' ? <p>{content3}</p> : content3}

            {expandable && isTruncated && (
                <Box sx={{ marginTop: '1rem' }}>
                    <Button
                        variant="contained"
                        sx={{
                            ...expandButtonSx,
                            alignSelf: 'flex-start',
                        }}
                        onClick={handleClick}
                    >
                        {isExpanded ? readLessLabel : readMoreLabel}
                    </Button>
                </Box>
            )}

            {buttonText && (
                <Box sx={{ marginTop: '1rem' }}>
                    <Button
                        variant="contained"
                        sx={{
                            ...buttonSx,
                            alignSelf: 'flex-start',
                        }}
                        href={buttonHref ?? ''}
                        target="_blank"
                    >
                        {buttonText}
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default TextContent;
